import { productTaxonomyRouteCatalog } from './renames';
import type { ProductTaxonomyRouteTemplate, ProductTaxonomyStage } from './types';

export type { ProductTaxonomyRouteTemplate } from './types';

export type ProductTaxonomyRouteKey = keyof typeof productTaxonomyRouteCatalog;

export function resolveProductTaxonomyRoute(
  key: ProductTaxonomyRouteKey,
  stage: ProductTaxonomyStage,
): ProductTaxonomyRouteTemplate {
  // `final` shares the `future` routes — only user-facing copy diverges.
  const variant = stage === 'final' ? 'future' : stage;
  return productTaxonomyRouteCatalog[key][variant];
}

// Every catalog template starts with this prefix (enforced by
// `ProductTaxonomyRouteTemplate`). Fragments are taken from just past it, because the
// `:workspaceId` param never appears literally in a live pathname while everything
// after it does.
const TAXONOMY_ROUTE_PREFIX = '/workspaces/:workspaceId';

// Future→current path-fragment replacements derived from the route catalog. Longest
// fragments run first (so nested renames replace before their prefixes) and we dedupe by
// future fragment so the handful of many-to-one renames resolve deterministically.
//
// Fragments are sliced off the shared prefix rather than off `/journeys`: most renames sit
// under `/journeys/...`, but the migrated Hydra pages (e.g. `/analysis/deliverability` →
// `/reports/deliverability`) have no `/journeys` segment at all, and slicing at a missing
// substring would silently yield a one-character fragment.
const JOURNEYS_PATHNAME_CANONICALIZATIONS: { future: string; current: string }[] = (() => {
  const seenFutureFragments = new Set<string>();
  const replacements: { future: string; current: string }[] = [];

  for (const { current, future } of Object.values(productTaxonomyRouteCatalog)) {
    const currentFragment = current.slice(TAXONOMY_ROUTE_PREFIX.length);
    const futureFragment = future.slice(TAXONOMY_ROUTE_PREFIX.length);

    if (futureFragment === currentFragment || seenFutureFragments.has(futureFragment)) {
      continue;
    }

    seenFutureFragments.add(futureFragment);
    replacements.push({ future: futureFragment, current: currentFragment });
  }

  return replacements.sort((a, b) => b.future.length - a.future.length);
})();

/**
 * Canonicalize a pathname to the current (pre-rename) vocabulary.
 *
 * Under the product-taxonomy rename flags, Journeys routes are served at their
 * future paths (e.g. `/journeys/automations`, `/journeys/profiles`,
 * `/reports/deliverability`). Route patterns across the app are written against the
 * current paths (`/journeys/campaigns`, `/journeys/people`,
 * `/analysis/deliverability`), so live pathnames are normalized here before
 * matching. Driven by the route catalog, so every rename — present and future,
 * `/journeys`-prefixed or not — is covered without per-rename code.
 */
export function normalizeJourneysPathname(pathname: string): string {
  for (const { future, current } of JOURNEYS_PATHNAME_CANONICALIZATIONS) {
    const index = pathname.indexOf(future);

    if (index === -1) {
      continue;
    }

    // Only replace at a segment boundary (end of path or a following `/`), so
    // e.g. `/journeys/reports` never matches inside `/journeys/reports-archive`.
    const nextChar = pathname[index + future.length];
    if (nextChar === undefined || nextChar === '/') {
      return pathname.slice(0, index) + current + pathname.slice(index + future.length);
    }
  }

  return pathname;
}
